<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Assign Order{{ orderIds.length > 1 ? 's' : '' }} to Carrier</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>

        <div class="modal-body">
            <div v-if="orderRatesFetchStatus === 'pending'" class="loading-container">
                <div class="order-info">
                    <skeleton height="20px" width="60%" border-radius="4px" />
                    <skeleton height="16px" width="80%" border-radius="4px" style="margin-top: 8px;" />
                </div>

                <div class="carrier-selection-skeleton">
                    <skeleton height="16px" width="40%" border-radius="4px" />
                    <div class="carrier-dropdown-skeleton">
                        <skeleton height="56px" width="100%" border-radius="8px" />
                    </div>
                </div>
            </div>

            <div v-else-if="orderRatesFetchStatus === 'error'" class="error-container">
                <md-icon class="error-icon">error</md-icon>
                <p>Failed to load carrier service rates. Please try again.</p>
                <md-button class="md-primary" @click="fetchOrderServiceRates">Retry</md-button>
            </div>

            <div v-else-if="orderRatesFetchStatus === STATE_MACHINE_STATUS.Idle">
                <div class="order-info">
                    <p>
                        <strong>{{ orderIds.length }}</strong>
                        order{{ orderIds.length > 1 ? 's' : '' }} selected for batch carrier assignment
                    </p>
                    <p v-if="orderIds.length > 1" class="batch-info">
                        Only carriers with services available for
                        <em>all</em>
                        selected orders will be shown.
                    </p>
                </div>

                <div v-if="availableServiceRates.length === 0" class="no-rates-container">
                    <md-icon class="info-icon">info</md-icon>
                    <p v-if="orderIds.length === 1">
                        No carrier service rates available for this order.
                    </p>
                    <p v-else>
                        No carriers have services available for all {{ orderIds.length }} selected orders. Try selecting
                        fewer orders or check individual order assignments.
                    </p>
                </div>

                <div v-else class="carrier-selection-container">
                    <label class="carrier-label">Select Carrier Service for Batch Assignment:</label>
                    <carrier-service-rate-dropdown
                        :carrier-service-rates="availableServiceRates"
                        :assigned-carrier-service-rate="null"
                        @assignedCarrierChanged="handleCarrierSelection"
                    />
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <md-button
                class="dialog-button md-primary"
                :disabled="!selectedCarrierService || isAssigning"
                @click="assignOrdersToCarrier"
            >
                <span v-if="isAssigning">Assigning...</span>
                <span v-else>Assign to Carrier</span>
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>

<script>
import { CarrierMarketplaceMixin } from '@/mixins/CarrierMarketplaceMixin';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { FEATURE_NAMES, STATE_MACHINE_STATUS } from '@/utils/constants';
import { FeatureManager } from '@/directives';
import CarrierServiceRateDropdown from './CarrierServiceRateDropdown';
import Skeleton from './Skeleton';

export default {
    name: 'AssignOrderToCarrierModal',
    computed: {
        STATE_MACHINE_STATUS() {
            return STATE_MACHINE_STATUS;
        }
    },
    components: {
        CarrierServiceRateDropdown,
        Skeleton
    },
    mixins: [CarrierMarketplaceMixin, GeneralMixin, FeatureManager],
    props: {
        /**
         * Array of order IDs to assign to carrier
         * @type {number[]}
         */
        orderIds: {
            type: Array,
            required: true,
            validator: (value) => {
                return Array.isArray(value) && value.length > 0 && value.every((id) => typeof id === 'number');
            }
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            orderRatesFetchStatus: STATE_MACHINE_STATUS.Idle,
            availableServiceRates: [],
            selectedCarrierService: null,
            isAssigning: false
        };
    },
    async mounted() {
        await this.fetchOrderServiceRates();
    },
    methods: {
        async fetchOrderServiceRates() {
            if (!this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace)) {
                this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Error;
                return;
            }

            if (this.orderIds.length === 0) {
                this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Idle;
                return;
            }

            this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Pending;
            try {
                const data = await this.$_carrierMarketplace_getOrderServiceRates(this.orderIds);
                if (!data || !data.orderServiceRates) {
                    this.availableServiceRates = [];
                    this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Idle;
                    return;
                }

                // Reduce all service rates from all orders into a single unified array
                // This creates a batch assignment where we find common carriers across all orders
                const carrierMap = new Map();

                data.orderServiceRates.forEach((orderRate) => {
                    orderRate.serviceRates.forEach((carrierRate) => {
                        const carrierId = carrierRate.teamCarrierId;

                        if (!carrierMap.has(carrierId)) {
                            // First time seeing this carrier, add it
                            carrierMap.set(carrierId, {
                                teamCarrierId: carrierRate.teamCarrierId,
                                carrierName: carrierRate.carrierName,
                                carrierLogoUrl: carrierRate.carrierLogoUrl,
                                serviceRates: [...carrierRate.serviceRates] // Clone the service rates
                            });
                        } else {
                            // Carrier already exists, merge/intersect service rates
                            const existingCarrier = carrierMap.get(carrierId);
                            const existingServiceCodes = new Set(
                                existingCarrier.serviceRates.map((sr) => sr.serviceCode)
                            );

                            // Only keep services that are available for this order too
                            const commonServices = carrierRate.serviceRates.filter((serviceRate) =>
                                existingServiceCodes.has(serviceRate.serviceCode)
                            );

                            existingCarrier.serviceRates = commonServices;
                        }
                    });
                });

                // Convert map to array and filter out carriers with no common services
                this.availableServiceRates = Array.from(carrierMap.values()).filter(
                    (carrier) => carrier.serviceRates.length > 0
                );
                this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Idle;
            } catch (error) {
                const message = 'Error in fetching service rates';
                showErrorMessage(this, message, error);
                this.orderRatesFetchStatus = STATE_MACHINE_STATUS.Error;
            }
        },

        handleCarrierSelection(eventData) {
            this.selectedCarrierService = eventData;
        },

        async assignOrdersToCarrier() {
            if (!this.selectedCarrierService) {
                return;
            }

            this.isAssigning = true;
            try {
                const request = {
                    orderIds: this.orderIds,
                    teamCarrierId: this.selectedCarrierService.teamCarrierId,
                    carrierServiceCode: this.selectedCarrierService.carrierServiceCode,
                    carrierServiceName: this.selectedCarrierService.carrierServiceName
                };

                const endpoint = `/api/carrier-jobs/assign`;
                const response = await handleRequests(endpoint, {
                    method: 'POST',
                    data: request
                });

                if (response.status === 200) {
                    this.$notifySuccess(
                        `Successfully assigned ${this.orderIds.length} order${
                            this.orderIds.length > 1 ? 's' : ''
                        } to carrier.`
                    );
                    this.resolve('success');
                } else {
                    throw new Error('Assignment failed');
                }
            } catch (error) {
                const message = 'Error in assigning orders to carrier';
                showErrorMessage(this, message, error);
            }
            this.isAssigning = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.modal-container {
    max-width: 600px;
    min-width: 500px;
}

.loading-container {
    padding: 24px;
}

.carrier-selection-skeleton {
    margin-top: 24px;

    .carrier-dropdown-skeleton {
        margin-top: 12px;
    }
}

.error-container,
.no-rates-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    text-align: center;
}

.error-icon,
.info-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.error-icon {
    color: #f44336;
}

.info-icon {
    color: #2196f3;
}

.order-info {
    background-color: #f5f5f5;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 24px;

    p {
        margin: 0 0 8px 0;
        color: #666;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .batch-info {
        font-size: 14px;
        color: #888;
        font-style: italic;
    }
}

.carrier-selection-container {
    .carrier-label {
        display: block;
        font-weight: 500;
        margin-bottom: 12px;
        color: #333;
    }
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #e0e0e0;
}

.dialog-button {
    min-width: 100px;
}
</style>
